import { Endpoint } from '@/api/common'
import type { Customer } from '@/api/customers-types'
import { useGetCustomer } from '@/api/react-query'
import { queryClient } from '@/providers/query-client'
import { customerSubject, customerToken, logIn as persistLogIn, logOut as persistLogOut } from '@/utils/auth'
import { useGetSubject } from '@/utils/hooks'
import { QueryObserverResult, useIsRestoring, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react'

const AuthContext = createContext<AuthContextValue>(undefined as any)

export type AuthContextValue = {
	isLoading: boolean
	authResolving: boolean
	refetch: () => Promise<QueryObserverResult<Customer, Error>>
	logIn: (token: string, customer: Customer) => void
} & (
	| {
			isLoggedIn: false
			token: undefined
			customer: undefined
	  }
	| {
			isLoggedIn: true
			token: string
			customer: Customer
	  }
)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const token = useGetSubject(customerToken)
	const storedCustomer = useGetSubject(customerSubject)

	const query = useGetCustomer({
		enabled: !!token
	})

	const queryClientHook = useQueryClient()

	useEffect(() => {
		if (query.data) {
			customerSubject.next(query.data)
		}
	}, [query.data])

	const isRestoring = useIsRestoring()
	useEffect(() => {
		if (isRestoring) return
		const cached = queryClientHook.getQueryData<Customer>([Endpoint.GetCustomer])
		if (token && !storedCustomer && cached) {
			customerSubject.next(cached)
		}
	}, [token, storedCustomer, isRestoring, queryClientHook])

	const refetch = useCallback(() => {
		queryClientHook.invalidateQueries({ queryKey: [Endpoint.GetCustomer] })
		return query.refetch()
	}, [queryClientHook, query])

	const logIn = useCallback(
		(token: string, customer: Customer) => {
			persistLogIn(token, customer)
			queryClientHook.invalidateQueries()
		},
		[queryClientHook]
	)

	const value = useMemo<AuthContextValue>(() => {
		if (!token || !storedCustomer) {
			return {
				isLoggedIn: false,
				token: undefined,
				customer: undefined,
				isLoading: query.isFetching,
				authResolving: !!token && !storedCustomer,
				refetch,
				logIn
			}
		}
		return {
			isLoggedIn: true,
			token,
			customer: storedCustomer,
			isLoading: query.isFetching,
			authResolving: false,
			refetch,
			logIn
		}
	}, [storedCustomer, query.isFetching, refetch, token, logIn])

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const logOut = () => {
	persistLogOut()
	queryClient.clear()
}

export const useLogOut = () => {
	const navigate = useNavigate()
	return useCallback(() => {
		logOut()
		navigate({ to: '/onboarding/welcome' })
	}, [navigate])
}

export const useAuth = () => {
	const value = useContext(AuthContext)
	if (!value) {
		throw new Error('useAuth must be used within an AuthProvider')
	}
	return value
}

export const useCustomer = () => {
	const auth = useAuth()
	if (!auth.isLoggedIn) {
		if (!auth.authResolving) {
			logOut()
		}
		throw new Error('Customer is not logged in')
	}
	return auth.customer
}
